import { LitElement, html, css } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import { SHARED_ELEMENTS, SHARED_CLASSES } from '../../styles';
import { icon } from '../../icons';
import {
  handleFileDownloadEvent,
  handleAdobeNavEvent,
  withBlockPlacement,
} from '../../../../helpers/adobe-analytics.js';
import { pushDataLayerEvent } from '../../../../helpers/data-layer.js';
import {
  CONTACT_REP_HASH,
  openContactRepModal,
} from '../../../../helpers/contact-rep-modal.js';

class CtaText extends LitElement {
  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      css`
        /* Host shrinks to inner <a> so the hover area matches the visible
           text — prevents dead zones when placed in a flex column or grid
           cell that would otherwise stretch it. Setting align-self here beats
           a container's align-items, so a block pairing a CTA with a header
           on one line has to set align-self: baseline on the cta-text. */
        :host {
          display: inline-block;
          align-self: start;
        }

        a {
          display: inline-flex;
          /* CTA tokens; ls -0.21 -> -0.24 is an intended micro-normalization. */
          font-size: var(--fs-cta);
          letter-spacing: var(--ls-cta);
          line-height: var(--lh-cta);
          white-space: nowrap;
          align-items: baseline;
          text-decoration: none;
        }

        /* Left-to-right sweep-in underline on hover — matches legacy nav-header pattern */
        h3::after {
          content: '';
          position: absolute;
          bottom: -0.15em;
          left: 0;
          width: 100%;
          height: 1px;
          background-color: currentColor;
          transform: scaleX(0);
          transform-origin: right center;
          transition: transform 0.25s ease-in;
          will-change: transform;
        }

        a:not(.disabled):hover h3::after,
        a:not(.disabled):active h3::after,
        a:not(.disabled):focus-visible h3::after {
          transform: scaleX(1);
          transform-origin: left center;
        }

        a.disabled {
          opacity: 0.35;
          cursor: default;
          pointer-events: none;
        }

        a:focus-visible {
          outline: 2px solid #ff993d;
          outline-offset: 2px;
        }

        h3 {
          position: relative;
          text-wrap: balance;
          font-size: calc(10rem / var(--default-rem-px-count));
          /* CTA text is a link label, not a heading, so its line-height
             is set here rather than inherited from the h3 default. */
          line-height: calc(16rem / var(--default-rem-px-count));

          .right-arrow {
            padding-left: calc(8rem / var(--default-rem-px-count));
            height: calc(9rem / var(--default-rem-px-count));
          }
        }

        a.large-font {
          align-items: center;

          h3 {
            font-size: calc(16rem / var(--default-rem-px-count));
            line-height: 166.667%;
            letter-spacing: -0.21px;
          }

          .right-arrow {
            padding-left: calc(16rem / var(--default-rem-px-count));
            height: calc(10rem / var(--default-rem-px-count));
          }
        }

        .download-icon {
          padding-right: calc(10rem / var(--default-rem-px-count));
        }

        .right-arrow {
          padding-left: calc(8rem / var(--default-rem-px-count));
          height: calc(9rem / var(--default-rem-px-count));
        }

        .white-font {
          color: #ffffff;
        }

        @media (min-width: 600px) {
          h3 {
            font-size: calc(14rem / var(--default-rem-px-count));
            /* Link-label line-height, independent of the h3 default. */
            line-height: calc(20rem / var(--default-rem-px-count));
            letter-spacing: normal;
            text-wrap: pretty;

            .right-arrow {
              padding-left: calc(8rem / var(--default-rem-px-count));
              height: inherit;
            }
          }

          a.large-font {
            h3 {
              font-size: calc(18rem / var(--default-rem-px-count));
              letter-spacing: -0.21px;
            }

            .right-arrow {
              padding-left: calc(16rem / var(--default-rem-px-count));
              height: calc(12rem / var(--default-rem-px-count));
            }
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      hidden: { type: Boolean },
      disabled: { type: Boolean },
      ctaData: { type: Object },
      ctaAnalyticsData: { type: Object },
    };
  }

  constructor() {
    super();
    this.ctaAnalyticsData = {};
  }

  render() {
    return !!this.parsedCtaData
      ? html`
          <a
            href=${this.urlString}
            target=${this.isTargetBlank}
            class="${classMap({
              'print-none': !this.reserveSpaceWhenHidden,
              'print-hidden': this.reserveSpaceWhenHidden,
              'white-font': this.useWhiteColors,
              'large-font': this.largeFontSize,
              disabled: this.disabled,
            })}"
            aria-disabled=${this.disabled ? 'true' : 'false'}
            @click="${this.handleClick}"
          >
            ${this.leftDownloadSvg
              ? html`
                  <span class="download-icon print-none">
                    ${icon('download')}
                  </span>
                `
              : html``}
            <h3
              class="${classMap({
                bold: !this.normalFontWeight,
              })}"
            >
              ${this.textString}
            </h3>
            ${this.renderRightArrowSVG
              ? html`
                  ${icon('arrow', 'right-arrow print-none')}
                `
              : html``}
          </a>
        `
      : html``;
  }

  get parsedCtaData() {
    if (this.ctaData) return this.ctaData;

    this.ctaData = this.getAttribute('cta-data')
      ? JSON.parse(this.getAttribute('cta-data'))
      : null;

    return this.ctaData;
  }

  handleClick(event) {
    // The hash is only read at page load, so the modal has to be opened here.
    if (this.urlString === CONTACT_REP_HASH) {
      openContactRepModal();
    }
    if (this.parsedCtaAnalyticsData) {
      this.sendAnalyticsDownloadEvent(this.ctaAnalyticsData, event);
    }
  }

  get parsedCtaAnalyticsData() {
    if (Object.keys(this.ctaAnalyticsData).length) return this.ctaAnalyticsData;

    this.ctaAnalyticsData = this.getAttribute('cta-analytics-data')
      ? JSON.parse(this.getAttribute('cta-analytics-data'))
      : {};

    this.ctaAnalyticsData.originalObject = this.parsedCtaData;
    return this.ctaAnalyticsData;
  }

  get renderRightArrowSVG() {
    return (
      !JSON.parse(this.getAttribute('hide-right-arrow-svg')) &&
      !!this.textString
    );
  }

  get textString() {
    return this.ctaData.text || '';
  }

  get urlString() {
    return this.ctaData.url || '';
  }

  get isTargetBlank() {
    return !!JSON.parse(this.getAttribute('target-blank')) ? '_blank' : '_self';
  }

  get leftDownloadSvg() {
    return JSON.parse(this.getAttribute('left-download-svg')) || false;
  }

  get reserveSpaceWhenHidden() {
    return this.hasAttribute('reserve-space-when-hidden');
  }

  get normalFontWeight() {
    return this.ctaData.notBold;
  }

  get largeFontSize() {
    return this.ctaData.fontSize === 'large';
  }

  get useWhiteColors() {
    return this.ctaData.useWhite;
  }

  sendAnalyticsDownloadEvent(downloadsAnalyticsData, event) {
    const ctaAnalyticsObj = withBlockPlacement(downloadsAnalyticsData, this);

    // Only real downloads; nav CTAs share this handler.
    if (ctaAnalyticsObj.isDownload) {
      const ga4Data = { ...ctaAnalyticsObj };
      delete ga4Data.adobe;
      pushDataLayerEvent('file_download', {
        download_source: 'cta',
        ...ga4Data,
      });
    }

    // Adobe Analytics
    ctaAnalyticsObj.originalObject = this.ctaData;

    if (ctaAnalyticsObj.isDownload) {
      handleFileDownloadEvent(ctaAnalyticsObj);
    } else {
      ctaAnalyticsObj.event = event;
      handleAdobeNavEvent(ctaAnalyticsObj);
    }
  }
}
customElements.define('cta-text', CtaText);
